import { useIsUserInRoles, useLanguage } from "@onesolution/hooks";

import { Avatar, Button } from "@heroui/react";
import { Icon } from "@iconify/react";
import {
	CalendarDate,
	getLocalTimeZone,
	parseAbsolute,
} from "@internationalized/date";
import { USER_ROLES } from "@onesolution/constants";
import {
	getOperationDetailsFromServer,
	type OperationDetails,
} from "@onesolution/serverFn";
import { FormDrawer, OpenMode } from "../FormDrawer/FormDrawer";
import OperationDetailsView, {
	type OperationDetailsTabs,
} from "./OperationDetails";
import OperationForm, {
	defaultOperationFormValues,
	type OperationFormData,
} from "./OperationForm";

const bufferDate = new CalendarDate(2020, 1, 1);

function OpertaionDetailsToOperationForm(
	mode: OpenMode,
	defaultValues: OperationFormData,
	operation: OperationDetails,
) {
	const formattedSubItems =
		operation.subitems?.map(
			({ originalId, name, quantity, deliveryDate, ids }) => ({
				product: { id: originalId, name },
				quantity,
				deliveryDate:
					deliveryDate && deliveryDate !== "null"
						? bufferDate
								.set(parseAbsolute(deliveryDate, getLocalTimeZone()))
								.copy()
						: null,
				ids,
			}),
		) || [];

	return {
		...defaultValues,
		...operation,
		deadline: operation.deadline
			? bufferDate
					.set(parseAbsolute(operation.deadline, getLocalTimeZone()))
					.copy()
			: null,
		timeline: operation.timelineOriginal
			? {
					start: bufferDate
						.set(
							parseAbsolute(
								operation.timelineOriginal.from,
								getLocalTimeZone(),
							),
						)
						.copy(),
					end: bufferDate
						.set(
							parseAbsolute(operation.timelineOriginal.to, getLocalTimeZone()),
						)
						.copy(),
				}
			: {},
		thumbnail: operation.thumbnail ? [operation.thumbnail] : undefined,
		campaigns: operation.campaigns?.linked_item_ids?.[0],
		marketingPlans: operation.marketingPlans?.linked_item_ids?.[0],
		services: operation.services?.linked_item_ids || [],
		description: operation.description,
		entities: operation.entities?.map(({ label }) => label) || [],
		existingProducts: formattedSubItems,
		requestedProducts: mode === OpenMode.edition ? [] : formattedSubItems,
	} as OperationFormData;
}

export default function OperationDrawer({
	onClose,
	selectedOpId,
	defaultOpenedTab,
	onEdit,
	mode,
	onlyChat,
}: {
	onClose: () => void;
	onEdit?: () => void;
	selectedOpId?: string;
	mode: OpenMode;
	onlyChat?: boolean;
	defaultOpenedTab?: OperationDetailsTabs;
}) {
	const { t } = useLanguage();
	const canEdit = useIsUserInRoles([USER_ROLES.Admin, USER_ROLES.Member]);
	const selectedOpQuery = getOperationDetailsFromServer.useQuery(
		{
			id: selectedOpId || "",
		},
		{ enabled: Boolean(selectedOpId) },
	);
	const finalOnlyChat = onlyChat || !selectedOpQuery.data?.canEdit;

	return (
		<FormDrawer
			closeOnClickOutside={false}
			onEdit={onEdit}
			canEdit={canEdit && !finalOnlyChat}
			openMode={mode}
			isLoading={selectedOpQuery.isFetching}
			selectedItem={
				selectedOpId && selectedOpQuery.data
					? OpertaionDetailsToOperationForm(
							mode,
							defaultOperationFormValues,
							selectedOpQuery.data,
						)
					: undefined
			}
			bodies={{
				[OpenMode.details]: () => (
					<OperationDetailsView
						onlyChat={finalOnlyChat}
						operation={selectedOpQuery.data}
						defaultOpenedTab={defaultOpenedTab}
					/>
				),
			}}
			headers={{
				[OpenMode.details]: (selectedItem) => {
					const op = selectedItem;

					if (!op) return null;
					const thumbnail =
						op.thumbnail?.[0] &&
						"url" in op.thumbnail[0] &&
						(op.thumbnail[0].url as string);

					return (
						<div className="w-full flex relative justify-between mx-4 items-center">
							{thumbnail && (
								<Avatar
									isBordered
									size="lg"
									radius="sm"
									onClick={() => window.open(thumbnail, "_blank")}
									className="hover:shadow-md transition-all cursor-pointer"
									src={thumbnail}
									showFallback={false}
								/>
							)}

							<div className="self-center">{op?.name.toUpperCase()}</div>
							{!finalOnlyChat && (
								<Button
									variant="flat"
									color="primary"
									startContent={<Icon icon="solar:folder-with-files-linear" />}
									onPress={() =>
										window.open(
											`/app/files?folderId=${op.boxFolderId}`,
											"_self",
										)
									}
								>
									{t("name.files-btn")}
								</Button>
							)}
						</div>
					);
				},
				[OpenMode.creation]: () =>
					`${t("basic.create")} ${t("name.operation")}`,
				[OpenMode.edition]: () => `${t("basic.edit")} ${t("name.operation")}`,
			}}
			onClose={onClose}
			form={({ loadedData, ...props }) => (
				<OperationForm {...props} loadedData={loadedData} />
			)}
		/>
	);
}
